// Phase 4.1 — Billing list table.
//
// Replaces the MUI <Table> + antd Dropdown/Menu action list in billing.tsx with
// the shared shadcn DataTable and a DropdownMenu. Presentation only: search +
// server pagination reuse the screen's handlers; the Invoice ID cell and the
// row action menu ("View"/"Edit") call the screen's existing `onView(item)` /
// `onEdit(item)` (same dispatch / same navigate state). The online-order marker
// icon and the ₹ amount formatting are preserved.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import AppConstant from "../../Globals/AppConstants";
import AppImages from "../../Globals/AppImages";

export interface BillingTableProps {
  data: any[];
  loading: boolean;
  onView: (item: any) => void;
  onEdit: (item: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  /** "" = All; otherwise the payment method reference name ("Cash", "Credit", ...). */
  paymentFilter?: string;
  onPaymentFilterChange?: (value: string) => void;
  /** Payment Method reference rows (referenceGroupId 4) used to build the filter list. */
  paymentOptions?: any[];
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
}

export function BillingTable({
  data,
  loading,
  onView,
  onEdit,
  searchValue,
  onSearch,
  searchPlaceholder,
  paymentFilter,
  onPaymentFilterChange,
  paymentOptions,
  title,
  headerActions,
  pagination,
}: BillingTableProps) {
  const navigate = useNavigate();
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "invoiceId",
        header: "Invoice ID",
        meta: { sticky: "left" },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <button
              type="button"
              onClick={() => onView(item)}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {item?.invoiceId}
              {item?.isOnlineOrder != 1 && <img src={AppImages.onlineOrderIcon} alt="" />}
            </button>
          );
        },
      },
      {
        id: "date",
        header: "Purchased Date",
        cell: ({ row }) => (row.original?.date ? moment(row.original?.date).format("DD-MMM-YYYY") : "-"),
      },
      {
        id: "customerName",
        header: "Customer Name",
        cell: ({ row }) => {
          const item = row.original;
          return item?.customerDetailId ? (
            <button
              type="button"
              onClick={() => navigate("/customer", { state: { auditRecordId: item.customerDetailId, auditSearchCode: item.mobileNumber } })}
              className="text-primary hover:underline text-left"
            >
              {item?.customerName || "-"}
            </button>
          ) : (
            item?.customerName
          );
        },
      },
      { id: "mobileNumberList", header: "Mobile Number", cell: ({ row }) => row.original?.mobileNumberList },
      { id: "itemsCount", header: "Items", cell: ({ row }) => <span className="tabular-nums">{row.original?.itemsCount}</span> },
      {
        id: "totalamount",
        header: "Total Amount",
        cell: ({ row }) => <span className="tabular-nums">₹ {row.original?.totalamount}</span>,
      },
      {
        id: "paymentMethodName",
        header: "Payment Type",
        cell: ({ row }) => row.original?.paymentMethodName || "-",
      },
      {
        id: "billedEmployeeName",
        header: "Billed Employee",
        cell: ({ row }) => row.original?.billedEmployeeName || "-",
      },
      {
        id: "createdBy",
        header: "Created By",
        cell: ({ row }) => (
          <span className="max-w-[120px] truncate block" title={row.original?.createdBy}>
            {row.original?.createdBy || "-"}
          </span>
        ),
      },
      {
        id: "createdOn",
        header: "Created On",
        cell: ({ row }) => (row.original?.createdOn ? moment(row.original?.createdOn).format("DD-MM-YY, hh:mm A") : "-"),
      },
      {
        id: "action",
        header: "Action",
        meta: { sticky: "right" },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Row actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onView(item)}>{AppConstant?.view}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(item)}>{AppConstant?.edit}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [onView, onEdit, navigate]
  );

  const paymentFilterToolbar = onPaymentFilterChange ? (
    <Select value={paymentFilter || "ALL"} onValueChange={onPaymentFilterChange}>
      <SelectTrigger className="w-[150px]"><SelectValue placeholder="Payment Type" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All</SelectItem>
        {(paymentOptions || []).map((item: any) => (
          <SelectItem key={item?.id} value={String(item?.name)}>{item?.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : undefined;

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      toolbar={paymentFilterToolbar}
      title={title}
      headerActions={headerActions}
      pagination={pagination}
    />
  );
}

export default BillingTable;
